/**
 * GUPM-329 — pivô: leituras atuais por canal → uma linha por marco superficial.
 *
 * Espelha `linhasDeTiltimetro` (GUPM-270), pelo mesmo motivo: o marco não cabe na
 * tabela principal do Resumo Técnico, que tem UMA coluna de leitura por linha, e ele
 * mede TRÊS grandezas por campanha topográfica.
 *
 * A diferença em relação ao tiltímetro é o que se exibe. Do tiltímetro mostra-se a
 * leitura (a inclinação é a grandeza). Do marco, a leitura é a coordenada UTM
 * absoluta — 296.462 m não diz nada a ninguém. O que interessa é o **deslocamento
 * desde a implantação**, em milímetros: ΔLeste, ΔNorte e Recalque.
 *
 * Domínio puro: sem React, sem Supabase, sem DOM.
 */
import type { Canal, Instrument, Reading } from '..';
import { deslocamentoDoMarco, type DeslocamentoMarco } from './deslocamento-marco';

/** Tipos cuja posição É a medição — a campanha topográfica é a leitura. */
export const TIPOS_MARCO = ['MS', 'MSE'] as const;

/**
 * Canais de uma campanha, na ordem em que a planilha os traz.
 *
 * Tipado como `Canal[]` — e não como tupla literal própria — de propósito: assim o
 * `tsc` recusa um canal que não exista no enum do banco. Espelha `CANAIS_TILT`.
 */
export const CANAIS_MARCO: readonly Canal[] = ['easting', 'northing', 'vertical'];

export interface LinhaMarco {
  instrument: Instrument;
  /** Deslocamento em mm desde a implantação; `null` por grandeza que falta. */
  deslocamento: DeslocamentoMarco;
  /**
   * Cota medida na campanha, em METROS — altitude absoluta, não deslocamento.
   *
   * Convive com `deslocamento.recalqueMm` de propósito: o recalque diz **quanto a
   * cota variou** desde a implantação (negativo = afundou), a cota diz **onde o
   * marco está**. É a cota que o RT compara com a crista. `null` quando a campanha não trouxe o canal.
   */
  cotaM: number | null;
  /** Data da campanha mais recente; `null` quando o marco nunca foi medido. */
  ultimaCampanha: Date | null;
}

/** `true` para marco superficial (a posição é a medição). */
export function ehMarco(type: string): boolean {
  return (TIPOS_MARCO as readonly string[]).includes(type);
}

/**
 * Monta uma linha por marco.
 *
 * `leiturasAtuais` é o conjunto de `readings_current` — já uma linha por instrumento
 * e canal. A referência de implantação vem do próprio cadastro
 * (`easting`/`northing`/`cotaTopo`), que é onde a carga a grava (o card decidiu que
 * ela **não** vira leitura: `readings.ts` é NOT NULL e posição de implantação não tem
 * data).
 *
 * Ausência nunca vira zero: marco sem campanha, ou sem referência, devolve `null` nas
 * grandezas que não puderam ser calculadas. Zero afirmaria "medimos e não houve
 * movimento" — o falso negativo que o GUPM-228 fechou para leitura ausente.
 */
export function linhasDeMarco(
  marcos: readonly Instrument[],
  leiturasAtuais: readonly Reading[],
): LinhaMarco[] {
  return marcos.map((instrument) => {
    const doInstrumento = leiturasAtuais.filter((r) => r.instrumentId === instrument.id);
    const valorDe = (canal: string): number | null =>
      doInstrumento.find((r) => r.canal === canal)?.value ?? null;

    const campanha = {
      easting: valorDe('easting'),
      northing: valorDe('northing'),
      cota: valorDe('vertical'),
    };

    const referencia =
      instrument.easting === null && instrument.northing === null && instrument.cotaTopo === null
        ? null
        : {
            easting: instrument.easting,
            northing: instrument.northing,
            cota: instrument.cotaTopo,
          };

    // A campanha mais recente entre os canais presentes. Os três chegam na mesma
    // campanha, mas usar o máximo evita afirmar uma data que um canal isolado não tem.
    const timestamps = doInstrumento
      .map((r) => r.ts)
      .filter((ts): ts is Date => ts instanceof Date);
    const ultimaCampanha =
      timestamps.length > 0
        ? new Date(Math.max(...timestamps.map((t) => t.getTime())))
        : null;

    return {
      instrument,
      deslocamento: deslocamentoDoMarco(referencia, campanha),
      // Direto da campanha, sem passar pela referência: a cota é medida, não
      // derivada. Marco sem posição de implantação não tem deslocamento, mas tem
      // altitude — anular as duas juntas esconderia um dado que o campo levantou.
      cotaM: campanha.cota,
      ultimaCampanha,
    };
  });
}
